import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useOptionUpdateHooks } from 'containers/ProductDetail/hooks/useOptionUpdateHooks';
import {
  Items,
  ProductParamInput,
  SalesType,
} from 'containers/ProductRegist/hooks/types';
import Column from 'components/common/Column';
import OptionBox from 'containers/ProductRegist/components/OptionSelectRegist/components/OptionBox';
import Row from 'components/common/Row';
import Text from 'components/common/Text';
import CheckBox from 'components/common/CheckBox';
import { ICON_PLUS_GRAY } from 'assets/icons';
import {
  InventoryTargetType,
  ItemState,
  Product,
  ProductState,
} from 'types/api';
import ModalContainer from 'containers/ProductRegist/components/common/ModalContainer';
import { ModifyValue } from 'containers/ProductDetail/hooks/useProductDetaillHooks';
import SaveButton from '../SaveButton';
import HackleManager from 'utils/hackle';
import { cloneDeep, flatten, isEqual } from 'lodash';
import ModalHeader from './ModalHeader';
import TitleContainer from 'containers/ProductRegist/components/TitleContainer';
import { isValidPurchasableCountToSave } from 'utils/purchaseLimit';

const OptionPlusButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #dbdcdf;
  background-color: #fff;

  color: #70737c;

  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */
  letter-spacing: -0.6px;
  cursor: pointer;
`;

interface OptionSelectModifyFormIProps {
  isMobile: boolean;
  productParamInput: ProductParamInput;
  productInfo: Product;
  modifyValue: ModifyValue;
  setModifyValue: Dispatch<SetStateAction<ModifyValue>>;
  handleOnChangeProductParamInput: (key: string, value: any) => void;
  handleRefetchProduct: (productId: string) => void;
}

const OptionSelectModifyForm: React.FC<
  OptionSelectModifyFormIProps
> = props => {
  const {
    isMobile,
    productParamInput,
    productInfo,
    modifyValue,
    setModifyValue,
    handleOnChangeProductParamInput,
    handleRefetchProduct,
  } = props;

  const [copyProductParamInput, setCopyProductParamInput] =
    useState<ProductParamInput>(cloneDeep(productParamInput));

  const handleOnChangeCopyProductParamInput = (key: string, value: any) => {
    setCopyProductParamInput(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const {
    items,
    selectedItems,
    setItems,
    handleAddOptions,
    handleSelectedItems,
    handleDeleteItmes,
    handleSetRepresentativeItems,
  } = useOptionUpdateHooks(
    copyProductParamInput,
    handleOnChangeCopyProductParamInput,
  );

  const isItemsChanged = !isEqual(
    productParamInput.items,
    copyProductParamInput.items,
  );

  const isItemOptionsChanged = !isEqual(
    flatten(productParamInput.items.map(item => item.options)),
    flatten(copyProductParamInput.items.map(item => item.options)),
  );

  const isValidItems = copyProductParamInput.items.every(copyItem => {
    if (
      copyItem.options?.length <= 1 ||
      copyItem.options?.length === undefined
    ) {
      return copyItem.name !== '';
    } else {
      return copyItem.options?.every(optionItem => optionItem.name !== '');
    }
  });

  const isAbled = (isItemsChanged || isItemOptionsChanged) && isValidItems;

  const [saleClosedFilter, setSaleClosedFilter] = useState<boolean>(false);

  const handleResetItems = () => {
    setItems(productParamInput.items);
    setCopyProductParamInput(productParamInput);
  };

  const handleRegistItems = () => {
    if (
      copyProductParamInput.items.some(
        item => item.price.retail < item.price.sale,
      )
    ) {
      window.showToast('정가는 판매가 보다 작을 수 없습니다.', 'failed');

      return;
    }

    if (
      copyProductParamInput.frip.attachedToSchedule &&
      copyProductParamInput.inventoryTargetType === InventoryTargetType.BY_ITEM
    ) {
      if (
        copyProductParamInput.items.some(item => item.minimumQuota > item.quota)
      ) {
        window.showToast(
          '최대 인원은 최소 인원보다 적을 수 없습니다. 최소 인원보다 높은 인원을 선택해주세요.',
          'failed',
        );

        return;
      }

      if (copyProductParamInput.items.some(item => item.quota === 0)) {
        window.showToast('최대인원은 0명으로 설정할 수 없어요', 'failed');

        return;
      }
    }

    if (
      !copyProductParamInput.items.some(
        item => item.status !== ItemState.CLOSED && item.representative,
      )
    ) {
      window.showToast('대표 옵션을 설정해주세요.', 'failed');

      return;
    }

    if (
      copyProductParamInput.items.some(
        item =>
          !isValidPurchasableCountToSave(
            item.maximumPurchasableCount,
            item.savedMaximumPurchasableCount,
          ),
      )
    ) {
      window.showToast('1회 구매 가능 수량은 1~10개로 설정해 주세요.', 'failed');

      return;
    }

    handleOnChangeProductParamInput('items', copyProductParamInput.items);
  };

  useEffect(() => {
    handleResetItems();
  }, [productParamInput]);

  useEffect(() => {
    HackleManager.logProductModifySalesOptionView(
      Number(productInfo.id),
      productInfo.title,
    );
  }, []);

  const renderOptionBox = (): JSX.Element => {
    return (
      <React.Fragment>
        {copyProductParamInput.items.map((item: Items, idx: number) => {
          return (
            <React.Fragment key={idx}>
              {!saleClosedFilter ? (
                item.status !== ItemState.CLOSED && (
                  <Column gap="16px" align="flex-end" key={idx}>
                    <OptionBox
                      type="modify"
                      idx={idx}
                      isMobile={isMobile}
                      item={item}
                      selectedItems={selectedItems}
                      productParamInput={copyProductParamInput}
                      savedRepresentative={
                        productParamInput.items.find(
                          saved => saved.id === item.id,
                        )?.representative ?? item.representative
                      }
                      setItems={setItems}
                      handleSelectedItems={handleSelectedItems}
                      handleDeleteItmes={handleDeleteItmes}
                      handleSetRepresentativeItems={
                        handleSetRepresentativeItems
                      }
                      handleRefetchProduct={handleRefetchProduct}
                    />
                  </Column>
                )
              ) : (
                <Column gap="16px" align="flex-end" key={idx}>
                  <OptionBox
                    type="modify"
                    idx={idx}
                    isMobile={isMobile}
                    item={item}
                    selectedItems={selectedItems}
                    productParamInput={copyProductParamInput}
                    savedRepresentative={
                      productParamInput.items.find(
                        saved => saved.id === item.id,
                      )?.representative ?? item.representative
                    }
                    setItems={setItems}
                    handleSelectedItems={handleSelectedItems}
                    handleDeleteItmes={handleDeleteItmes}
                    handleSetRepresentativeItems={handleSetRepresentativeItems}
                    handleRefetchProduct={handleRefetchProduct}
                  />
                </Column>
              )}
            </React.Fragment>
          );
        })}
      </React.Fragment>
    );
  };

  const renderItems = (): JSX.Element => {
    return (
      <Column gap="16px" align="flex-start">
        <Row
          justifyContent="space-between"
          style={{
            width: '100%',
          }}
        >
          <Text color="#46474c" size="14px">
            총 옵션 {items.length}개
          </Text>
          <Row gap="6px">
            <CheckBox
              isChecked={saleClosedFilter}
              onChange={() => {
                setSaleClosedFilter(!saleClosedFilter);
              }}
              style={{ width: '16px', height: '16px' }}
            />
            <Text>판매중지된 옵션 보기</Text>
          </Row>
        </Row>
        {renderOptionBox()}
        <Column align="flex-end">
          <OptionPlusButton onClick={handleAddOptions}>
            <img src={ICON_PLUS_GRAY} alt="plus icon" />
            옵션추가
          </OptionPlusButton>
        </Column>
      </Column>
    );
  };

  const renderContents = (): JSX.Element => {
    if (isMobile) {
      return (
        <ModalContainer
          isAbled={productInfo.status !== ProductState.CLOSED && isAbled}
          isMobile={isMobile}
          onCloseText=" "
          onAcceptText="적용"
          onClose={() => {
            setModifyValue({ ...modifyValue, modifyStatus: '' });
          }}
          onClick={() => {
            if (isAbled) {
              HackleManager.logProductModifyItemSaveComplete(
                Number(productInfo.id),
                productInfo.title,
                '옵션',
              );

              handleRegistItems();

              setModifyValue({
                ...modifyValue,
                isModify: true,
                modifyStatus: '',
              });
            }
          }}
        >
          <Column style={{ padding: '0 20px' }}>
            <ModalHeader
              title="옵션 수정하기"
              modifyValue={modifyValue}
              setModifyValue={setModifyValue}
            />
            <Column>{renderItems()}</Column>
          </Column>
        </ModalContainer>
      );
    }

    return (
      <Column style={{ padding: '0 40px' }}>
        <TitleContainer title="옵션 수정하기" isMobile={isMobile} />
        <Column gap="24px">
          {renderItems()}
          {productInfo.status !== ProductState.CLOSED && (
            <SaveButton
              isAbled={isAbled}
              modifyValue={modifyValue}
              setModifyValue={setModifyValue}
              handleResetItem={handleResetItems}
              handleRegistItem={() => {
                HackleManager.logProductModifyItemSaveComplete(
                  Number(productInfo.id),
                  productInfo.title,
                  '옵션',
                );
                handleRegistItems();
              }}
            />
          )}
        </Column>
      </Column>
    );
  };

  return renderContents();
};

export default OptionSelectModifyForm;
